import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getMainImage } from '../../../utils/image-utils';
import { getApiEndpoint } from '../../shared/utils/api-url';

interface Product {
  id: number;
  name: string;
  description?: string;
  price: number;
  original_price?: number;
  images?: { main: string; colors?: { [color: string]: string } };
  discount_percent?: number;
}

function SearchBar() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    // Debounce da busca
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchQuery]);

  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/products?active=true&search=${encodeURIComponent(query)}`));
      
      // Verificar se a resposta é JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Resposta não é JSON');
      }
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar produtos: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Parse images se for string
      const parsedData = data.map((product: any) => {
        if (typeof product.images === 'string') {
          try {
            product.images = JSON.parse(product.images);
          } catch (e) {
            product.images = { main: product.images || '' };
          }
        }
        return product;
      });

      setResults(parsedData.slice(0, 5)); // Limitar a 5 resultados
      setShowResults(true);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalogo?search=${encodeURIComponent(searchQuery)}`);
      setShowResults(false);
      setSearchQuery('');
    }
  };

  const handleProductClick = (productId: number) => {
    navigate(`/produto/${productId}`);
    setShowResults(false);
    setSearchQuery('');
  };

  const handleAddToCart = (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    
    const cartItem = {
      id: Date.now(),
      product_id: product.id,
      name: product.name,
      price: product.price,
      color: null,
      size: null,
      quantity: 1,
      image: getMainImage(product.images),
    };
    
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    existingCart.push(cartItem);
    localStorage.setItem('cart', JSON.stringify(existingCart));
    
    // Atualizar contador do carrinho
    const event = new CustomEvent('cartUpdated');
    window.dispatchEvent(event);
    
    setShowResults(false);
    setSearchQuery('');
  };

  const formatPrice = (price: number) => {
    return `R$ ${price.toFixed(2).replace('.', ',')}`;
  };

  const calculateDiscount = (price: number, originalPrice: number) => {
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  return (
    <div className="search-bar-container" ref={searchRef}>
      <form className="search-bar" onSubmit={handleSubmit}>
        <i className="fas fa-search search-icon"></i>
        <input
          type="text"
          placeholder="Buscar produtos..."
          className="search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => {
            if (results.length > 0) {
              setShowResults(true);
            }
          }}
        />
        {loading && (
          <div className="search-loading">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
        )}
      </form>

      {showResults && results.length > 0 && (
        <div className="search-results">
          {results.map((product) => {
            const mainImage = getMainImage(product.images);
            const discount = product.original_price && product.price < product.original_price
              ? calculateDiscount(product.price, product.original_price)
              : null;

            return (
              <div
                key={product.id}
                className="search-result-item"
                onClick={() => handleProductClick(product.id)}
              >
                <img
                  src={mainImage}
                  alt={product.name}
                  className="search-result-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = `data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='60' height='60' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle' font-family='Arial, sans-serif' font-size='10' fill='%23999'%3EProduto%3C/text%3E%3C/svg%3E`;
                  }}
                />
                <div className="search-result-info">
                  <h4 className="search-result-name">{product.name}</h4>
                  {product.description && (
                    <p className="search-result-description">
                      {product.description.length > 60
                        ? product.description.substring(0, 60) + '...'
                        : product.description}
                    </p>
                  )}
                  <div className="search-result-price">
                    <span className="search-result-price-current">{formatPrice(product.price)}</span>
                    {product.original_price && product.original_price > product.price && (
                      <>
                        <span className="search-result-price-old">
                          {formatPrice(product.original_price)}
                        </span>
                        {discount && (
                          <span className="search-result-discount">-{discount}%</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <button
                  className="search-result-add-btn"
                  onClick={(e) => handleAddToCart(e, product)}
                  title="Adicionar ao carrinho"
                >
                  <i className="fas fa-shopping-cart"></i>
                </button>
              </div>
            );
          })}
          {results.length > 0 && (
            <Link
              to={`/catalogo?search=${encodeURIComponent(searchQuery)}`}
              className="search-result-view-all"
              onClick={() => {
                setShowResults(false);
                setSearchQuery('');
              }}
            >
              Ver todos os resultados
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;

